import { useEffect, useState, useCallback } from "react";
import { redirect } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";

export type Role = "learner" | "parent" | "teacher" | "admin";
export type Grade =
  | "P1" | "P2" | "P3" | "P4" | "P5" | "P6" | "P7"
  | "S1" | "S2" | "S3" | "S4" | "S5" | "S6";

export const PRIMARY_GRADES: Grade[] = ["P1","P2","P3","P4","P5","P6","P7"];
export const SECONDARY_GRADES: Grade[] = ["S1","S2","S3","S4","S5","S6"];

export interface Session {
  userId: string;
  name: string;
  role: Role;
  grade?: Grade;
  school?: string;
  linkCode?: string;
  phone?: string;
  photoUrl?: string;
  language?: string;
}

const KEY = "makai.session";

function read(): Session | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as Session) : null;
  } catch {
    return null;
  }
}

function write(s: Session | null) {
  if (typeof window === "undefined") return;
  if (s) window.localStorage.setItem(KEY, JSON.stringify(s));
  else window.localStorage.removeItem(KEY);
  window.dispatchEvent(new CustomEvent("makai:session"));
}

/** Synchronous read of cached session. Available immediately on the client. */
export function getSession(): Session | null {
  return read();
}

/** Clears local cache. Use signOut() to also end the Supabase session. */
export function setSession(s: Session | null) {
  write(s);
}

/**
 * In-memory request coalescing for session/role lookups.
 *
 * Every screen mounts `useSession()` and every guarded route calls
 * `requireRole()`, which previously issued a fresh `profiles` + `user_roles`
 * round trip per mount (measured: ~1.1k role reads / ~800 profile reads per
 * session). Results are verified server-side, then reused for a short window
 * and shared between concurrent callers. Nothing is persisted, so a page
 * reload always re-verifies.
 */
const FRESH_MS = 30_000;
let sessionInflight: Promise<Session | null> | null = null;
let sessionFetchedAt = 0;
let roleCache: { userId: string; role: Role; at: number } | null = null;

/** Drop cached verification (sign-in/out, role change). */
export function invalidateSessionCache() {
  sessionInflight = null;
  sessionFetchedAt = 0;
  roleCache = null;
}

async function fetchSession(): Promise<Session | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) { write(null); return null; }

  const [{ data: profile }, { data: roleRow }] = await Promise.all([
    supabase.from("profiles").select("*").eq("id", user.id).maybeSingle(),
    supabase.from("user_roles").select("role").eq("user_id", user.id).limit(1).maybeSingle(),
  ]);

  if (!profile || !roleRow) { write(null); return null; }

  const session: Session = {
    userId: user.id,
    name: profile.full_name || user.email?.split("@")[0] || "Learner",
    role: roleRow.role as Role,
    grade: (profile.grade as Grade | null) ?? undefined,
    school: profile.school_name ?? undefined,
    linkCode: profile.link_code ?? undefined,
    phone: profile.phone ?? undefined,
    photoUrl: profile.photo_url ?? undefined,
    language: profile.preferred_language ?? "en",
  };
  roleCache = { userId: user.id, role: session.role, at: Date.now() };
  write(session);
  return session;
}

let sessionPending = false;

export async function refreshSession(opts?: { force?: boolean }): Promise<Session | null> {
  if (opts?.force) invalidateSessionCache();
  const fresh = Date.now() - sessionFetchedAt < FRESH_MS;
  // Reuse the in-flight request, or a very recent result, for all callers.
  if (sessionInflight && (sessionPending || fresh)) return sessionInflight;

  sessionPending = true;
  const p = fetchSession().finally(() => {
    sessionPending = false;
    sessionFetchedAt = Date.now();
  });
  sessionInflight = p;
  return p;
}

export async function signOut() {
  invalidateSessionCache();
  await supabase.auth.signOut();
  write(null);
}

export function useSession() {
  const [session, setLocal] = useState<Session | null>(() => read());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    // Initial hydrate from Supabase (deduped/cached across mounts)
    refreshSession().then((s) => {
      if (mounted) { setLocal(s); setLoading(false); }
    });

    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_OUT") { invalidateSessionCache(); write(null); setLocal(null); return; }
      if (event === "SIGNED_IN" || event === "USER_UPDATED") {
        // Defer to avoid deadlock with the auth state callback
        setTimeout(() => { refreshSession({ force: true }).then((s) => { if (mounted) setLocal(s); }); }, 0);
      }
    });

    const onLocal = () => setLocal(read());
    window.addEventListener("makai:session", onLocal);
    window.addEventListener("storage", onLocal);

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
      window.removeEventListener("makai:session", onLocal);
      window.removeEventListener("storage", onLocal);
    };
  }, []);

  const update = useCallback((s: Session | null) => write(s), []);
  return { session, loading, setSession: update, signOut, refresh: refreshSession };
}

export function isPrimary(grade?: Grade): boolean {
  return !!grade && PRIMARY_GRADES.includes(grade);
}

export function roleHome(role: Role): string {
  switch (role) {
    case "learner": return "/learner";
    case "parent":  return "/parent";
    case "teacher": return "/teacher";
    case "admin":   return "/admin";
  }
}

/**
 * Route guard: ensures the current user is signed in AND has the given role.
 * Verifies against Supabase (not just the localStorage cache) so a tampered
 * cache cannot grant access to another role's pages.
 *
 * - No session → redirect to /auth
 * - Wrong role → redirect to that user's own role home
 */
export async function requireRole(role: Role): Promise<Session> {
  // Offline-first: if the browser reports no connection and we already have a
  // cached session with a matching role, let the user continue. The Supabase
  // client still attaches the persisted access token for any requests that do
  // run, and `sync-engine` will revalidate as soon as connectivity returns.
  const cached = read();
  const offline = typeof navigator !== "undefined" && navigator.onLine === false;
  if (offline) {
    if (!cached) throw redirect({ to: "/auth" });
    if (cached.role !== role) throw redirect({ to: roleHome(cached.role) });
    return cached;
  }

  let user: { id: string } | null = null;
  try {
    const { data } = await supabase.auth.getUser();
    user = data.user;
  } catch {
    // Network failure mid-check — fall back to cache if we have one.
    if (cached && cached.role === role) return cached;
    throw redirect({ to: "/auth" });
  }
  if (!user) {
    write(null);
    throw redirect({ to: "/auth" });
  }

  // Reuse a recently server-verified role for this exact user instead of
  // re-querying on every navigation (in-memory only, 30s window).
  let actualRole: Role | undefined;
  if (roleCache && roleCache.userId === user.id && Date.now() - roleCache.at < FRESH_MS) {
    actualRole = roleCache.role;
  } else {
    const { data: roleRow } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", user.id)
      .limit(1)
      .maybeSingle();
    actualRole = roleRow?.role as Role | undefined;
    if (actualRole) roleCache = { userId: user.id, role: actualRole, at: Date.now() };
  }

  if (!actualRole) {
    write(null);
    throw redirect({ to: "/auth" });
  }

  if (actualRole !== role) {
    throw redirect({ to: roleHome(actualRole) });
  }

  const session = await refreshSession();
  if (!session) {
    // Profile fetch hiccuped but auth is valid — trust the cache if compatible.
    if (cached && cached.role === role) return cached;
    throw redirect({ to: "/auth" });
  }
  return session;
}

/**
 * For public pages like /auth — if already signed in, bounce to role home.
 */
export async function redirectIfAuthed(): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;
  const { data: roleRow } = await supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", user.id)
    .limit(1)
    .maybeSingle();
  const r = roleRow?.role as Role | undefined;
  if (r) throw redirect({ to: roleHome(r) });
}

